import { z } from 'zod';
import { UuidSchema } from './common';
import { MessageStatusSchema } from './messages';

export const PeriodPresetSchema = z.enum(['today', '7d', '30d', '90d']);
export type PeriodPreset = z.infer<typeof PeriodPresetSchema>;

export const OverviewKpisSchema = z.object({
  messagesSent: z.number().int().nonnegative(),
  deliveryRate: z.number().min(0).max(1),
  activeConnections: z.number().int().nonnegative(),
  failures24h: z.number().int().nonnegative(),
});
export type OverviewKpis = z.infer<typeof OverviewKpisSchema>;

export const MessagesByDayPointSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD'),
  sent: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type MessagesByDayPoint = z.infer<typeof MessagesByDayPointSchema>;

export const MessagesByDaySchema = z.object({
  points: z.array(MessagesByDayPointSchema),
});
export type MessagesByDay = z.infer<typeof MessagesByDaySchema>;

export const StatusBreakdownPointSchema = z.object({
  status: MessageStatusSchema,
  count: z.number().int().nonnegative(),
  percentage: z.number().min(0).max(100),
});
export type StatusBreakdownPoint = z.infer<typeof StatusBreakdownPointSchema>;

export const StatusBreakdownSchema = z.object({
  points: z.array(StatusBreakdownPointSchema),
  total: z.number().int().nonnegative(),
});
export type StatusBreakdown = z.infer<typeof StatusBreakdownSchema>;

export const MessagesByConnectionPointSchema = z.object({
  connectionId: UuidSchema,
  label: z.string().min(1),
  count: z.number().int().nonnegative(),
});
export type MessagesByConnectionPoint = z.infer<typeof MessagesByConnectionPointSchema>;

export const MessagesByConnectionSchema = z.object({
  points: z.array(MessagesByConnectionPointSchema),
});
export type MessagesByConnection = z.infer<typeof MessagesByConnectionSchema>;

/**
 * Overview MoneyBack-style — 5 KPIs centrais da tela `/analytics`.
 * Espelha "Disparos Gerais / Faturamento / Conversão / Pix Gerados / Falhas"
 * documentado em research/analise-revenue-vs-moneyback.md.
 *
 * - `messagesSent`: msgs com status final positivo (sent+delivered+read) no período.
 * - `totalRevenueCents`: soma de receita atribuída a leads convertidos no período (filter por convertedAt).
 * - `conversionRate`: leads convertidos / total leads no período (0..1).
 * - `pixGenerated`: webhook_events de bob-payments com event_type transaction.created/pending no período.
 * - `failures`: msgs com status='failed' no período.
 */
export const MoneyBackOverviewSchema = z.object({
  messagesSent: z.number().int().nonnegative(),
  totalRevenueCents: z.number().int().nonnegative(),
  conversionRate: z.number().min(0).max(1),
  pixGenerated: z.number().int().nonnegative(),
  failures: z.number().int().nonnegative(),
});
export type MoneyBackOverview = z.infer<typeof MoneyBackOverviewSchema>;

/**
 * Performance IA vs humano. Compara dois eixos:
 * - Conversas atendidas (`conversations.assignedTo`).
 * - Mensagens outbound enviadas (`conversation_messages.authorType`).
 * - Conversões atribuídas (lead.status='converted' cuja phone bateu com
 *   conversations atendidas pelo respectivo eixo no período).
 * - Receita atribuída (sum de `leads.totalRevenueCents` para os leads
 *   convertidos atribuíveis ao eixo).
 *
 * Atribuição é "first-touch by handler" — se o lead foi convertido e tinha
 * uma conversation aberta com `assignedTo='ai'`, conta pra IA. Multi-touch
 * cross-handler é pendência fase 3+.
 */
export const AgentPerformanceSchema = z.object({
  conversationsHandled: z.number().int().nonnegative(),
  messagesSent: z.number().int().nonnegative(),
  leadsConverted: z.number().int().nonnegative(),
  revenueCents: z.number().int().nonnegative(),
});
export type AgentPerformance = z.infer<typeof AgentPerformanceSchema>;

export const AgentsPerformanceSchema = z.object({
  ai: AgentPerformanceSchema,
  human: AgentPerformanceSchema,
});
export type AgentsPerformance = z.infer<typeof AgentsPerformanceSchema>;
